import * as React from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { useNavigate } from '@tanstack/react-router'
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CalendarDays,
  Check,
  Eye,
  EyeOff,
  Loader2,
  MapPin,
  Search,
  Sparkles,
  Undo2,
  UserPlus,
  X,
} from 'lucide-react'
import {
  GENDERS,
  MODES,
  PROVINCES,
  SEGMENT_BY_AGENT_USERNAME,
  SEGMENT_LABEL,
  SHIFT_PRIORITIES,
  WORK_HOURS,
  parseDob,
  type AutofillResult,
  type AutofillSkipped,
  type CityOption,
  type CustomerDetail,
  type CustomerDraft,
  type Gender,
  type Mode,
  type Province,
  type ShiftPriority,
  type WorkHours,
} from '@acs/shared'
import { ApiError } from '@/lib/api'
import {
  AUTOFILL_LABEL,
  planAutofill,
  type ApplyMode,
  type AutofillKey,
  type AutofillPatch,
  type AutofillValues,
} from '@/lib/autofill'
import { cn } from '@/lib/cn'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { useToast } from '@/components/ui/toast'
import { AutofillButton, AutofillDialog } from '@/components/customers/autofill-dialog'
import { useFeatures } from '@/hooks/use-auth'
import { useCustomerDetail } from '@/hooks/use-customers'
import {
  fieldErrorsOf,
  friendlyError,
  useAgentRoster,
  useCities,
  useCreateCustomer,
  useDuplicateCheck,
  useUpdateCustomer,
  type CustomerEdit,
} from '@/hooks/use-create-customer'

/**
 * Create a customer.
 *
 * The one screen in this console that writes to the shared cluster, so the
 * shape of the form is decided by what the write needs rather than by what
 * looks tidy:
 *
 *   - The order is the order the data arrives in a handover — who they are,
 *     how we sign in as them, where they will work, then the paperwork.
 *   - Only the four fields the system cannot function without are required, and
 *     each says WHY on screen rather than only turning red when submitted.
 *   - Nothing is validated on the keystroke. Errors appear on blur and on
 *     submit, because a field that goes red while you are still typing the
 *     second character of an email address is telling you off for not having
 *     finished.
 *
 * ── Performance ─────────────────────────────────────────────────────
 * One reducer holds the whole draft, which means every keystroke re-renders
 * this component. That is the right trade at eighteen text inputs and would be
 * the wrong one at the city list, which is sixty rows with checkboxes — so the
 * picker is memoised and receives only the four things it paints. Typing a name
 * does not touch it.
 */

/* ================================================================== *
 * Draft state
 * ================================================================== */

interface Draft {
  name: string
  email: string
  phone: string
  pin: string
  imapAppPassword: string
  cities: string[]
  /** city key → chosen sites. An empty array means "all of them". */
  warehouses: Record<string, string[]>
  workHours: WorkHours[]
  /** '' = not picked; one of `SHIFT_PRIORITIES` once it is. Required on save. */
  shiftPriority: string
  agentUsername: string
  notes: string
  sinNumber: string
  dateOfBirth: string
  /** '' = not set. Required before the audio assessment can run; see below. */
  gender: string
  addressLine1: string
  addressLine2: string
  addressCity: string
  addressProvince: string
  addressPostalCode: string
  mode: Mode
}

const EMPTY: Draft = {
  name: '',
  email: '',
  phone: '',
  pin: '',
  imapAppPassword: '',
  cities: [],
  warehouses: {},
  workHours: [],
  shiftPriority: '',
  agentUsername: '',
  notes: '',
  sinNumber: '',
  dateOfBirth: '',
  gender: '',
  addressLine1: '',
  addressLine2: '',
  addressCity: '',
  addressProvince: '',
  addressPostalCode: '',
  mode: '3',
}

/** Every text field, so `set` can be typed without eighteen action variants. */
type TextField = {
  [K in keyof Draft]: Draft[K] extends string ? K : never
}[keyof Draft]

type Action =
  | { type: 'set'; field: TextField; value: string }
  | { type: 'mode'; value: Mode }
  /** `sites` is the city's full list — selecting a city selects all of them. */
  | { type: 'toggleCity'; key: string; sites: readonly string[] }
  | { type: 'toggleSite'; key: string; site: string; all: readonly string[] }
  | { type: 'setSites'; key: string; sites: string[] }
  | { type: 'clearCities' }
  | { type: 'toggleHours'; value: WorkHours }
  /** Everything a pasted message had, at once. See `planAutofill`. */
  | { type: 'autofill'; patch: AutofillPatch }
  /** Replace the whole draft — used once, when an edit's customer loads. */
  | { type: 'load'; draft: Draft }
  | { type: 'reset' }

function reducer(state: Draft, action: Action): Draft {
  switch (action.type) {
    case 'set': {
      if (state[action.field] === action.value) return state
      return { ...state, [action.field]: action.value }
    }
    case 'mode':
      return state.mode === action.value ? state : { ...state, mode: action.value }

    /*
     * Selecting a city selects ALL of its warehouses, explicitly.
     *
     * The list is never "empty means all". That encoding is ambiguous the
     * moment somebody deselects their way back down to nothing — the selection
     * would silently flip from "none" to "everything", which is the opposite of
     * what the last click asked for. It also matches the collection: a stored
     * `city_warehouses` always enumerates its sites.
     */
    case 'toggleCity': {
      const warehouses = { ...state.warehouses }
      if (state.cities.includes(action.key)) {
        delete warehouses[action.key]
        return { ...state, cities: state.cities.filter((c) => c !== action.key), warehouses }
      }
      warehouses[action.key] = [...action.sites]
      return { ...state, cities: [...state.cities, action.key], warehouses }
    }

    /** Removes exactly the one clicked, and leaves the rest alone. */
    case 'toggleSite': {
      const current = state.warehouses[action.key] ?? [...action.all]
      const next = current.includes(action.site)
        ? current.filter((s) => s !== action.site)
        // Re-adding restores the city's own order rather than appending, so the
        // stored array is the same whatever order the toggles were clicked in.
        : action.all.filter((s) => s === action.site || current.includes(s))
      return { ...state, warehouses: { ...state.warehouses, [action.key]: next } }
    }

    case 'setSites':
      return { ...state, warehouses: { ...state.warehouses, [action.key]: action.sites } }

    case 'clearCities':
      return state.cities.length === 0 ? state : { ...state, cities: [], warehouses: {} }

    case 'toggleHours': {
      const has = state.workHours.includes(action.value)
      return {
        ...state,
        workHours: has
          ? state.workHours.filter((h) => h !== action.value)
          // Kept in the canonical order rather than click order, so the stored
          // JSON is comparable between two customers.
          : WORK_HOURS.filter((h) => h === action.value || state.workHours.includes(h)),
      }
    }

    /*
     * One step for everything the message had — one render, and one entry
     * for Undo to put back, rather than fifteen. The patch never carries an
     * `undefined` (see `planAutofill`), so the spread cannot blank a field.
     */
    case 'autofill':
      return { ...state, ...action.patch }

    case 'load':
      return action.draft

    case 'reset':
      return EMPTY
  }
}

/* ================================================================== *
 * Validation
 *
 * A mirror of the server's zod schema, not a replacement for it — the server
 * validates again and is the only thing that can check the email is unused.
 * This exists so the operator hears about a typo before a round trip, and so
 * the submit button can say what is missing rather than just being disabled.
 * ================================================================== */

type Errors = Partial<Record<keyof Draft, string>>

/** Deliberately permissive. The server does the real check; this catches typos. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
/** Canadian, space optional — the column holds both "M1M 2Z9" and "M1M2Z9". */
const POSTAL = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/

/**
 * Every rule here mirrors one in the server's zod schema, and the shapes came
 * from the collection rather than from convention: all 604 pins are six
 * digits, 600 of 603 app passwords are sixteen characters with no whitespace,
 * 387 of 392 SINs are nine digits.
 *
 * Address line 2 and Notes are the only two fields that may be left blank.
 */
function validate(d: Draft, isEdit = false): Errors {
  const e: Errors = {}

  if (d.name.trim() === '') e.name = 'Required'
  else if (d.name.trim().length > 120) e.name = 'Must be at most 120 characters'

  const email = d.email.trim()
  if (email === '') e.email = 'Required'
  else if (!EMAIL.test(email)) e.email = 'Not a valid email address'

  const phone = d.phone.replace(/\D/g, '')
  if (d.phone.trim() === '') e.phone = 'Required'
  else if (!(phone.length === 10 || (phone.length === 11 && phone.startsWith('1')))) {
    e.phone = 'Must be a 10-digit number'
  }

  /*
   * The three write-only fields are REQUIRED on create and optional on edit.
   *
   * They are never sent back to the browser, so an edit form shows them blank —
   * and demanding they be retyped to save a change of address would mean going
   * to find the customer's PIN to correct a postcode. Blank means keep; a value
   * still has to be the right shape.
   */
  if (d.pin.trim() === '') {
    if (!isEdit) e.pin = 'Required — sign-in cannot start without it'
  } else if (!/^\d{6}$/.test(d.pin.trim())) e.pin = 'Must be 6 digits'

  // Compared after stripping, because Google shows the password in groups of
  // four and that is what gets pasted.
  const app = d.imapAppPassword.replace(/\s+/g, '')
  if (app === '') {
    if (!isEdit) e.imapAppPassword = 'Required — the OTP is read from this inbox'
  } else if (app.length !== 16) e.imapAppPassword = 'Must be the 16-character Google app password'

  if (d.agentUsername === '') e.agentUsername = 'Required'

  if (d.cities.length === 0) e.cities = 'Pick at least one city'
  else if (d.cities.some((c) => (d.warehouses[c] ?? []).length === 0)) {
    e.cities = 'Every city needs at least one warehouse'
  }

  if (d.workHours.length === 0) e.workHours = 'Pick at least one'
  // Required beside every choice of hours, part-time included — the sibling
  // panel that shares the column asks the same, so the two agree on what a
  // complete record is. Server rule: `shiftPriority: z.enum(SHIFT_PRIORITIES)`.
  if (d.shiftPriority === '') e.shiftPriority = 'Pick one'

  const sin = d.sinNumber.replace(/\D/g, '')
  if (d.sinNumber.trim() === '') {
    if (!isEdit) e.sinNumber = 'Required'
  } else if (sin.length !== 9) e.sinNumber = 'Must be 9 digits'

  /*
   * Three checks, in the order that produces the most useful message.
   *
   * `isRealDate` matters because the segments happily assemble `31/02/1999`,
   * and `Date.parse` rolls that forward to 3 March — so without it the form
   * would accept a date nobody typed and store it.
   */
  if (d.dateOfBirth === '') e.dateOfBirth = 'Required'
  else if (!isRealDate(d.dateOfBirth)) e.dateOfBirth = 'Not a real date'
  else if (d.dateOfBirth >= today()) e.dateOfBirth = 'Must be in the past'
  else if (d.dateOfBirth < '1900-01-01') e.dateOfBirth = 'Check the year'

  if (d.addressLine1.trim() === '') e.addressLine1 = 'Required'
  if (d.addressCity.trim() === '') e.addressCity = 'Required'
  if (d.addressProvince === '') e.addressProvince = 'Required'

  if (d.addressPostalCode.trim() === '') e.addressPostalCode = 'Required'
  else if (!POSTAL.test(d.addressPostalCode.trim())) e.addressPostalCode = 'Must look like M1M 2Z9'

  return e
}

/** Used by the footer to name what is still outstanding. */
const FIELD_LABEL: Partial<Record<keyof Draft, string>> = {
  name: 'Name',
  email: 'Email',
  phone: 'Phone',
  pin: 'PIN',
  imapAppPassword: 'Gmail app password',
  agentUsername: 'Agent',
  cities: 'Cities',
  workHours: 'Work hours',
  shiftPriority: 'Shift priority',
  sinNumber: 'SIN',
  dateOfBirth: 'Date of birth',
  gender: 'Gender',
  addressLine1: 'Address',
  addressLine2: 'Address line 2',
  addressCity: 'City',
  addressProvince: 'Province',
  addressPostalCode: 'Postal code',
}

/* ================================================================== *
 * Layout primitives
 * ================================================================== */

function Section(p: {
  title: string
  hint?: string
  icon?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="rounded-lg border border-line bg-s1">
      <header className="flex items-baseline gap-2 border-b border-line px-4 py-2.5">
        {p.icon ? (
          <span aria-hidden="true" className="self-center text-faint [&_svg]:size-[15px]">
            {p.icon}
          </span>
        ) : null}
        <h2 className="text-[13.5px] font-semibold text-fg">{p.title}</h2>
        {p.hint ? <p className="min-w-0 flex-1 truncate text-[12px] text-faint">{p.hint}</p> : null}
      </header>
      <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 p-4 md:grid-cols-2">{p.children}</div>
    </section>
  )
}

/**
 * The hint and the error a control points at.
 *
 * `aria-describedby` may name an element that is not on the page — the spec
 * says to ignore a dangling id — so both are listed unconditionally and only
 * one is ever rendered. That is what lets the association be static: the
 * alternative is threading a computed id from `Field` into `children`, which
 * cannot be done to a `ReactNode` without `cloneElement`.
 *
 * This was a `data-described-by` attribute on a wrapper div, which associates
 * nothing with anything: the error was coloured but never announced, so a
 * screen-reader user heard "PIN, required, edit text" and no reason.
 */
function describedBy(id: string): string {
  return `${id}-hint ${id}-error`
}

/**
 * One labelled control.
 *
 * The label is a real `<label htmlFor>` rather than a wrapper, because several
 * of these hold a control that is itself composed of buttons — a wrapping label
 * would make clicking any of them toggle the first one.
 */
function Field(p: {
  id: string
  label: string
  required?: boolean
  hint?: string
  error?: string | undefined
  /** Span both columns of the section grid. */
  wide?: boolean
  children: React.ReactNode
}) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', p.wide && 'md:col-span-2')}>
      {/* `id` as well as `htmlFor`: a control made of several inputs is a
          `role="group"` that names itself with `aria-labelledby`, not something
          `htmlFor` can point at. See `DateOfBirthField`. */}
      <label
        id={`${p.id}-label`}
        htmlFor={p.id}
        className="flex items-center gap-1 text-[11px] font-semibold tracking-[0.05em] text-muted uppercase"
      >
        {p.label}
        {p.required ? (
          <span className="text-danger" title="Required">
            *
          </span>
        ) : null}
      </label>

      {p.children}

      {p.error ? (
        /* `role="alert"` so the message is announced when it appears on submit,
           not only when focus happens to land back on the field. */
        <p
          id={`${p.id}-error`}
          role="alert"
          className="flex items-center gap-1 text-[11.5px] text-danger"
        >
          <AlertTriangle aria-hidden="true" className="size-3 flex-none" />
          {p.error}
        </p>
      ) : p.hint ? (
        <p id={`${p.id}-hint`} className="text-[11.5px] leading-snug text-faint">
          {p.hint}
        </p>
      ) : null}
    </div>
  )
}

/** The shell a native control wears, so a `<select>` matches an `<Input>`. */
const CONTROL =
  'h-10 w-full rounded-md border border-line bg-s1 px-3 text-[14.5px] text-fg outline-none md:h-9 ' +
  'transition-colors duration-100 hover:border-line-strong ' +
  'focus:border-accent focus:ring-2 focus:ring-accent/25'

/** One pill of a choice group — the work hours and the shift priority. */
const PILL =
  'flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12.5px] font-medium transition-colors'
const PILL_ON = 'border-accent bg-accent-soft text-accent'
const PILL_OFF = 'border-line text-muted hover:border-line-strong hover:text-fg'

/**
 * A secret, with a reveal toggle.
 *
 * `autoComplete="new-password"` on both: without it the browser offers to fill
 * the operator's OWN saved credentials into a form that creates somebody else,
 * which is both wrong and a way to leak one.
 */
const SecretField = React.memo(function SecretField(p: {
  /** Doubles as the state key, so the field cannot be wired to the wrong one. */
  id: TextField
  value: string
  invalid: boolean
  placeholder?: string
  /*
   * `(field, value)` rather than `(value)`.
   *
   * A `(v) => set('pin', v)` closure at the call site is a new function on every
   * render, which defeats this `memo` completely — the component would re-render
   * on every keystroke in every other field on the page, toggling a `useState`
   * owner for nothing. Passing the key through keeps both handlers referentially
   * stable for the life of the form.
   */
  onChange: (field: TextField, value: string) => void
  onBlur: (field: TextField) => void
}) {
  const [shown, setShown] = React.useState(false)
  const toggle = React.useCallback(() => setShown((s) => !s), [])

  return (
    <div className="relative">
      <Input
        id={p.id}
        type={shown ? 'text' : 'password'}
        value={p.value}
        placeholder={p.placeholder}
        autoComplete="new-password"
        spellCheck={false}
        aria-invalid={p.invalid || undefined}
        aria-describedby={describedBy(p.id)}
        onChange={(e) => p.onChange(p.id, e.target.value)}
        onBlur={() => p.onBlur(p.id)}
        className={cn('pr-9', p.invalid && 'border-danger focus-within:border-danger')}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={toggle}
        aria-label={shown ? 'Hide' : 'Show'}
        aria-pressed={shown}
        className={cn(
          'absolute top-1/2 right-1 -translate-y-1/2 rounded-sm p-1.5 text-faint',
          'transition-colors hover:text-fg focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:outline-none'
        )}
      >
        {shown ? <EyeOff aria-hidden="true" className="size-4" /> : <Eye aria-hidden="true" className="size-4" />}
      </button>
    </div>
  )
})

/* ================================================================== *
 * Date of birth
 *
 * A native `<input type="date">` opens its calendar on the CURRENT month, and
 * a birth date is twenty-odd years away — so entering 1999 meant clicking
 * through three hundred months, or discovering the small year dropdown hidden
 * in the header. Defaulting that calendar to 1999 would only move the problem
 * to whoever was born in 1985.
 *
 * The fix is to drop the calendar. Browsing is the wrong interaction for a
 * value the operator already knows: they are reading it off an ID card, not
 * choosing it. Three typed segments take eight keystrokes and no mouse —
 * `02` `04` `1999` — and the year is typed rather than scrolled to, so no
 * default year is needed at all.
 *
 * This is the GOV.UK / Stripe date-of-birth pattern, and it is what the field
 * already looked like: the placeholder said `dd-mm-yyyy`.
 * ================================================================== */

interface DateParts {
  day: string
  month: string
  year: string
}

const NO_DATE: DateParts = { day: '', month: '', year: '' }

function splitIso(iso: string): DateParts {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  return m ? { year: m[1] as string, month: m[2] as string, day: m[3] as string } : NO_DATE
}

/** `''` until all three are complete — a half-typed date is not a date. */
function joinIso(p: DateParts): string {
  if (p.day.length !== 2 || p.month.length !== 2 || p.year.length !== 4) return ''
  return `${p.year}-${p.month}-${p.day}`
}

/** `2` -> `02`, so `2/4/1999` is accepted as readily as `02/04/1999`. */
function pad2(v: string): string {
  return v.length === 1 ? `0${v}` : v
}

type Segment = 'day' | 'month' | 'year'

/** Left to right, so "the next box" is an index rather than a special case. */
const SEGMENTS: readonly Segment[] = ['day', 'month', 'year']
const SEGMENT_LENGTH: Readonly<Record<Segment, number>> = { day: 2, month: 2, year: 4 }

/**
 * Can this segment still grow?
 *
 * `4` cannot begin a two-digit day and `2` cannot begin a two-digit month, so
 * those are finished the moment they are typed and the caret should move on —
 * `5` `4` `1999` is a whole date in six keystrokes. `3` is NOT finished, because
 * 30 and 31 exist; nor is `1` in a month, because of 10, 11 and 12. Getting
 * this list wrong is not a missed optimisation, it is a caret that jumps away
 * mid-number.
 */
function segmentDone(segment: Segment, value: string): boolean {
  if (value.length === SEGMENT_LENGTH[segment]) return true
  if (value.length !== 1) return false
  if (segment === 'day') return value >= '4'
  if (segment === 'month') return value >= '2'
  return false
}

const SEGMENT_CLASS =
  'h-10 rounded-md border bg-s1 text-center text-[14.5px] tabular text-fg outline-none md:h-9 ' +
  'transition-colors duration-100 hover:border-line-strong placeholder:text-faint ' +
  'focus:border-accent focus:ring-2 focus:ring-accent/25'

/**
 * Three typed boxes, behaving as one control.
 *
 * ── The bug this shape is built around ──────────────────────────────
 * Typing `21` into the day box produced `02`. The cause was a stale closure,
 * and it is worth naming because it is invisible in review: the change handler
 * called `focus()` on the next box SYNCHRONOUSLY, which blurred the day box
 * before React had re-rendered — so the blur handler ran with the `parts` from
 * the previous render, where the day was still `2`, padded that to `02`, and
 * wrote it back over the `21` that had just been set.
 *
 * Every handler here therefore reads the latest value from a ref rather than
 * from its own closure. State is still the source of truth for rendering; the
 * ref exists only so that two handlers firing in the same tick cannot disagree
 * about what is in the boxes.
 */
const DateOfBirthField = React.memo(function DateOfBirthField(p: {
  id: string
  /** `YYYY-MM-DD`, or `''`. */
  value: string
  invalid: boolean
  onChange: (iso: string) => void
  onBlur: () => void
}) {
  const [parts, setParts] = React.useState<DateParts>(() => splitIso(p.value))

  /** The same value as `parts`, readable before the next render. */
  const latest = React.useRef<DateParts>(parts)
  const boxes = React.useRef<(HTMLInputElement | null)[]>([])

  const { onChange, onBlur } = p

  const commit = React.useCallback(
    (next: DateParts) => {
      latest.current = next
      setParts(next)
      onChange(joinIso(next))
    },
    [onChange]
  )

  /*
   * Accept an external reset without clobbering a half-typed date.
   *
   * Clear sets the parent's value to `''` while the boxes still hold a whole
   * date, so the two disagree and the boxes are rebuilt. While typing, the
   * parent is also `''` — but so is `joinIso(parts)`, so they agree and nothing
   * is touched. Comparing the DERIVED value rather than the raw one is what
   * makes both cases work under a single rule.
   */
  React.useEffect(() => {
    if (joinIso(latest.current) === p.value) return
    const next = splitIso(p.value)
    latest.current = next
    setParts(next)
  }, [p.value])

  const focusBox = React.useCallback((index: number, caret: 'start' | 'end') => {
    const box = boxes.current[index]
    if (!box) return
    box.focus()
    const at = caret === 'end' ? box.value.length : 0
    // Safari throws on setSelectionRange for some input types; a caret that
    // lands in the default place is not worth breaking the whole handler for.
    try {
      box.setSelectionRange(at, at)
    } catch {
      /* ignore */
    }
  }, [])

  const onSegmentChange = React.useCallback(
    (index: number, raw: string) => {
      const segment = SEGMENTS[index] as Segment
      const max = SEGMENT_LENGTH[segment]
      const digits = raw.replace(/\D/g, '').slice(0, max)

      const full = digits.length === max
      const done = segmentDone(segment, digits)

      // Pad only when leaving early on a single digit — `5` in the day box is
      // stored as `05`, but `2` is left alone because it may still become `21`.
      commit({ ...latest.current, [segment]: done && !full ? pad2(digits) : digits })

      if (done && index < SEGMENTS.length - 1) focusBox(index + 1, 'end')
    },
    [commit, focusBox]
  )

  /**
   * Padding happens when the caret LEAVES a box, not when it fills.
   *
   * So `2` becomes `02` on the way out, while `2` is still `2` for as long as
   * the operator might be about to type the `1` of `21`.
   */
  const onSegmentBlur = React.useCallback(
    (index: number) => {
      const segment = SEGMENTS[index] as Segment
      if (segment === 'year') return
      const current = latest.current[segment]
      const padded = pad2(current)
      if (padded !== current) commit({ ...latest.current, [segment]: padded })
    },
    [commit]
  )

  const onKeyDown = React.useCallback(
    (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
      const box = event.currentTarget
      const atStart = box.selectionStart === 0 && box.selectionEnd === 0
      const atEnd = box.selectionStart === box.value.length && box.selectionEnd === box.value.length

      // Backspace in an empty box steps back rather than doing nothing — the
      // whole control has to feel like one field, and this is the keystroke
      // that gives it away when it does not.
      if (event.key === 'Backspace' && box.value === '' && index > 0) {
        event.preventDefault()
        focusBox(index - 1, 'end')
        return
      }
      if (event.key === 'ArrowLeft' && atStart && index > 0) {
        event.preventDefault()
        focusBox(index - 1, 'end')
        return
      }
      if (event.key === 'ArrowRight' && atEnd && index < SEGMENTS.length - 1) {
        event.preventDefault()
        focusBox(index + 1, 'start')
      }
    },
    [focusBox]
  )

  /*
   * A pasted date fills all three boxes.
   *
   * Operators paste `02/04/1999`, `02-04-1999` and `1999-04-02` out of a
   * spreadsheet. Without this the digits land in whichever box had focus and
   * are truncated to two — silently producing a different date rather than a
   * visible failure.
   */
  const onPaste = React.useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const text = event.clipboardData.getData('text').trim()
      const ymd = /^(\d{4})\D(\d{1,2})\D(\d{1,2})$/.exec(text)
      const dmy = /^(\d{1,2})\D(\d{1,2})\D(\d{4})$/.exec(text)
      if (!ymd && !dmy) return
      event.preventDefault()
      commit(
        ymd
          ? { year: ymd[1] as string, month: pad2(ymd[2] as string), day: pad2(ymd[3] as string) }
          : { day: pad2(dmy?.[1] as string), month: pad2(dmy?.[2] as string), year: dmy?.[3] as string }
      )
      focusBox(SEGMENTS.length - 1, 'end')
    },
    [commit, focusBox]
  )

  /**
   * "Touched" means the operator left the whole control, not one box.
   *
   * Marking it on each box's blur made the error appear as soon as the caret
   * moved from `dd` to `mm` — telling somebody their date of birth is invalid
   * while they are two thirds of the way through typing it.
   */
  const onGroupBlur = React.useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      const next = event.relatedTarget as Node | null
      if (next !== null && event.currentTarget.contains(next)) return
      onBlur()
    },
    [onBlur]
  )

  const emitted = joinIso(parts)
  const border = p.invalid ? 'border-danger' : 'border-line'

  return (
    <div
      role="group"
      aria-labelledby={`${p.id}-label`}
      aria-describedby={describedBy(p.id)}
      onBlur={onGroupBlur}
      className="flex items-center gap-1.5"
    >
      {SEGMENTS.map((segment, index) => (
        <React.Fragment key={segment}>
          {index > 0 ? (
            <span aria-hidden="true" className="text-faint">
              /
            </span>
          ) : null}
          <input
            {...(index === 0 ? { id: p.id } : {})}
            ref={(el) => {
              boxes.current[index] = el
            }}
            value={parts[segment]}
            inputMode="numeric"
            autoComplete="off"
            placeholder={segment === 'year' ? 'yyyy' : segment === 'month' ? 'mm' : 'dd'}
            aria-label={segment === 'day' ? 'Day' : segment === 'month' ? 'Month' : 'Year'}
            aria-invalid={p.invalid || undefined}
            maxLength={SEGMENT_LENGTH[segment]}
            onPaste={onPaste}
            onFocus={(e) => e.currentTarget.select()}
            onKeyDown={(e) => onKeyDown(index, e)}
            onChange={(e) => onSegmentChange(index, e.target.value)}
            onBlur={() => onSegmentBlur(index)}
            className={cn(SEGMENT_CLASS, border, segment === 'year' ? 'w-[4.25rem]' : 'w-12')}
          />
        </React.Fragment>
      ))}

      <DateCalendar value={emitted} invalid={p.invalid} onPick={onChange} />

      {/* The assembled date, so the operator can confirm at a glance that
          02/04/1999 was read as April and not February. */}
      <span className="ml-1 min-w-0 truncate text-[12px] text-faint">{longDate(emitted)}</span>
    </div>
  )
})

const LONG_DATE = new Intl.DateTimeFormat('en-CA', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
})

function longDate(iso: string): string {
  if (iso === '') return ''
  const ms = Date.parse(`${iso}T00:00:00Z`)
  if (Number.isNaN(ms)) return ''
  // Round-trip check: `2026-02-31` parses to 3 March, which would be a
  // confident and wrong confirmation of what was typed.
  const d = new Date(ms)
  if (d.toISOString().slice(0, 10) !== iso) return ''
  return LONG_DATE.format(d)
}

/* ------------------------------------------------------------------ *
 * The calendar, beside the boxes rather than instead of them
 *
 * Typing is still the fast path — this exists for the case the boxes cannot
 * serve: a date read off a form as "the second Tuesday", or an operator who
 * would rather point than type.
 *
 * It is NOT the native calendar, and the difference is the whole reason the
 * boxes exist. A native picker opens on the current month and offers one arrow
 * per month, so 1999 is three hundred clicks away. Here the month and the year
 * are dropdowns in the header: any date in the last century is three clicks,
 * and the year list opens on 1999 when the field is empty — which is the
 * default that was asked for, in the one place where a default helps.
 * ------------------------------------------------------------------ */

/** Empty-field default. Most customers in the collection were born near it. */
const DEFAULT_VIEW_YEAR = 1999
/** A range wide enough for anyone who could hold a job, and no wider. */
const EARLIEST_YEAR = 1940

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

/** Days in a month, leap years included — `new Date(y, m, 0)` is day 0 of the next. */
function daysInMonth(year: number, monthIndex: number): number {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate()
}

/** Weekday of the 1st, 0 = Sunday, so the grid knows how many blanks to lead with. */
function firstWeekday(year: number, monthIndex: number): number {
  return new Date(Date.UTC(year, monthIndex, 1)).getUTCDay()
}

function isoOf(year: number, monthIndex: number, day: number): string {
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

const DateCalendar = React.memo(function DateCalendar(p: {
  /** `YYYY-MM-DD`, or `''`. */
  value: string
  invalid: boolean
  onPick: (iso: string) => void
}) {
  const [open, setOpen] = React.useState(false)

  const selected = React.useMemo(() => (isRealDate(p.value) ? splitIso(p.value) : null), [p.value])

  /*
   * Where the grid is looking. Seeded from the value when there is one, and
   * re-seeded each time the popover opens — otherwise reopening it after
   * picking a date would still show wherever the operator last browsed to.
   */
  const [viewYear, setViewYear] = React.useState(DEFAULT_VIEW_YEAR)
  const [viewMonth, setViewMonth] = React.useState(0)

  React.useEffect(() => {
    if (!open) return
    if (selected) {
      setViewYear(Number(selected.year))
      setViewMonth(Number(selected.month) - 1)
    } else {
      setViewYear(DEFAULT_VIEW_YEAR)
      setViewMonth(0)
    }
  }, [open, selected])

  const thisYear = new Date().getUTCFullYear()
  const years = React.useMemo(() => {
    const out: number[] = []
    // Newest first: a birth year is far likelier to be recent than 1940.
    for (let y = thisYear; y >= EARLIEST_YEAR; y -= 1) out.push(y)
    return out
  }, [thisYear])

  const total = daysInMonth(viewYear, viewMonth)
  const lead = firstWeekday(viewYear, viewMonth)
  const todayIso = today()

  const pick = React.useCallback(
    (day: number) => {
      p.onPick(isoOf(viewYear, viewMonth, day))
      setOpen(false)
    },
    [p, viewYear, viewMonth]
  )

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label="Choose the date from a calendar"
          className={cn(
            'flex size-9 flex-none items-center justify-center rounded-md border bg-s1 text-muted',
            'transition-colors hover:border-line-strong hover:text-fg',
            'focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:outline-none',
            'data-[state=open]:border-accent data-[state=open]:text-accent',
            p.invalid ? 'border-danger' : 'border-line'
          )}
        >
          <CalendarDays aria-hidden="true" className="size-4" />
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          className={cn(
            'z-[80] w-[17.5rem] rounded-lg border border-line bg-s1 p-2.5 shadow-e3',
            'transition-opacity duration-100 starting:opacity-0'
          )}
        >
          {/* Month and year as selects — the reason this is usable at all.
              Native selects also give type-ahead, so "1985" jumps there. */}
          <div className="flex gap-1.5 pb-2">
            <select
              value={viewMonth}
              onChange={(e) => setViewMonth(Number(e.target.value))}
              aria-label="Month"
              className="h-8 min-w-0 flex-1 rounded-md border border-line bg-s1 px-2 text-[13px] text-fg outline-none focus:border-accent"
            >
              {MONTH_NAMES.map((name, i) => (
                <option key={name} value={i}>
                  {name}
                </option>
              ))}
            </select>
            <select
              value={viewYear}
              onChange={(e) => setViewYear(Number(e.target.value))}
              aria-label="Year"
              className="h-8 w-[5.25rem] flex-none rounded-md border border-line bg-s1 px-2 text-[13px] tabular text-fg outline-none focus:border-accent"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          <div role="grid" aria-label={`${MONTH_NAMES[viewMonth]} ${viewYear}`}>
            <div role="row" className="grid grid-cols-7 pb-1">
              {WEEKDAYS.map((d) => (
                <span
                  key={d}
                  role="columnheader"
                  aria-label={d}
                  className="text-center text-[10.5px] font-medium text-faint"
                >
                  {d}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-0.5">
              {/* Blanks so the 1st lands under its weekday. */}
              {Array.from({ length: lead }, (_, i) => (
                <span key={`lead-${i}`} aria-hidden="true" />
              ))}

              {Array.from({ length: total }, (_, i) => {
                const day = i + 1
                const iso = isoOf(viewYear, viewMonth, day)
                const isSelected = iso === p.value
                // A birth date cannot be today or later; showing those as
                // pressable and then rejecting them on submit is a worse
                // answer than not offering them.
                const disabled = iso >= todayIso
                return (
                  <button
                    key={day}
                    type="button"
                    disabled={disabled}
                    aria-pressed={isSelected}
                    onClick={() => pick(day)}
                    className={cn(
                      'h-8 rounded-md text-[12.5px] tabular transition-colors',
                      'focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:outline-none',
                      disabled
                        ? 'cursor-not-allowed text-faint/40'
                        : isSelected
                          ? 'bg-accent font-medium text-accent-fg'
                          : 'text-muted hover:bg-s3 hover:text-fg'
                    )}
                  >
                    {day}
                  </button>
                )
              })}
            </div>
          </div>

          {p.value === '' ? null : (
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={() => {
                  p.onPick('')
                  setOpen(false)
                }}
                className="text-[12px] text-muted underline-offset-2 hover:text-fg hover:underline"
              >
                Clear
              </button>
            </div>
          )}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
})

/* ================================================================== *
 * City & warehouse picker
 *
 * The legacy screen is a 60-row scrolling checkbox list beside an empty panel
 * that fills up as you tick things. It has one real problem: choosing WHICH
 * warehouses is a second, invisible step, so almost every customer in the data
 * ends up with every site in their city — the default nobody knew they were
 * accepting.
 *
 * Here the two steps are on screen together. The left column picks cities, the
 * right column is one card per chosen city showing its sites as toggles, with
 * "All" as an explicit, visible state rather than an accident.
 * ================================================================== */

const CityPicker = React.memo(function CityPicker(p: {
  catalogue: readonly CityOption[]
  loading: boolean
  /**
   * Distinct from an empty catalogue, and the distinction is the whole point.
   *
   * The server used to answer `200 []` when it could not reach the database, so
   * this panel said "no sites are published" during an outage — a confident,
   * wrong answer that sends the operator looking in the wrong place. The API now
   * fails instead, and this renders the failure as a failure.
   */
  failed: boolean
  onRetry: () => void
  selected: string[]
  warehouses: Record<string, string[]>
  dispatch: React.Dispatch<Action>
  error?: string | undefined
}) {
  const [needle, setNeedle] = React.useState('')

  const selectedSet = React.useMemo(() => new Set(p.selected), [p.selected])

  const filtered = React.useMemo(() => {
    const q = needle.trim().toLowerCase()
    if (q === '') return p.catalogue
    // Matches the label AND the site codes, so "YYZ" finds the cities that fly
    // out of Toronto — which is how the operator's paperwork names them.
    return p.catalogue.filter(
      (c) => c.key.includes(q) || c.sites.some((s) => s.toLowerCase().includes(q))
    )
  }, [p.catalogue, needle])

  const chosen = React.useMemo(
    () => p.selected.map((key) => p.catalogue.find((c) => c.key === key)).filter((c): c is CityOption => c !== undefined),
    [p.selected, p.catalogue]
  )

  // The selection is explicit, so this is a plain sum — it used to fall back to
  // `c.sites.length` for an empty list, which counted a city with nothing
  // selected as if everything were.
  const totalSites = React.useMemo(
    () => chosen.reduce((n, c) => n + (p.warehouses[c.key]?.length ?? 0), 0),
    [chosen, p.warehouses]
  )

  return (
    <div className="md:col-span-2">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* ── cities ─────────────────────────────────────────────── */}
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="flex items-center gap-1 text-[11px] font-semibold tracking-[0.05em] text-muted uppercase">
              Cities
              <span className="text-danger" title="Required">
                *
              </span>
            </span>
            <span className="text-[11px] text-faint tabular">
              {p.selected.length > 0 ? `${p.selected.length} selected` : `${p.catalogue.length} available`}
            </span>
          </div>

          <Input
            value={needle}
            onChange={(e) => setNeedle(e.target.value)}
            onClear={() => setNeedle('')}
            placeholder="Search a city or a SITE code…"
            icon={<Search aria-hidden="true" />}
            aria-label="Search cities"
            spellCheck={false}
          />

          <div
            role="group"
            aria-label="Cities"
            className={cn(
              'h-[17rem] overflow-auto overscroll-contain rounded-md border border-line bg-s2 p-1',
              // 60 rows of checkbox, most of them off-screen. Skipping their
              // layout and paint while scrolled out is free here because every
              // row is the same height, so the placeholder is exact.
              '[&>*]:[content-visibility:auto] [&>*]:[contain-intrinsic-size:auto_30px]'
            )}
          >
            {p.loading ? (
              <p className="flex h-full items-center justify-center gap-2 text-[12.5px] text-faint">
                <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
                Loading the catalogue…
              </p>
            ) : p.failed ? (
              <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
                <AlertTriangle aria-hidden="true" className="size-4 text-warn" />
                <p className="text-[12.5px] text-muted">The catalogue could not be loaded.</p>
                <button
                  type="button"
                  onClick={p.onRetry}
                  className="text-[12px] text-accent underline-offset-2 hover:underline"
                >
                  Try again
                </button>
              </div>
            ) : filtered.length === 0 ? (
              <p className="flex h-full items-center justify-center px-4 text-center text-[12.5px] text-faint">
                {p.catalogue.length === 0
                  ? 'The catalogue is empty — no sites are published.'
                  : `No city matches “${needle.trim()}”.`}
              </p>
            ) : (
              filtered.map((city) => (
                <CityRow
                  key={city.key}
                  city={city}
                  checked={selectedSet.has(city.key)}
                  dispatch={p.dispatch}
                />
              ))
            )}
          </div>

          {p.error ? (
            <p role="alert" className="flex items-center gap-1 text-[11.5px] text-danger">
              <AlertTriangle aria-hidden="true" className="size-3 flex-none" />
              {p.error}
            </p>
          ) : null}
        </div>

        {/* ── warehouses ─────────────────────────────────────────── */}
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[11px] font-semibold tracking-[0.05em] text-muted uppercase">
              Warehouses
            </span>
            {chosen.length > 0 ? (
              <button
                type="button"
                onClick={() => p.dispatch({ type: 'clearCities' })}
                className="text-[11px] text-muted underline-offset-2 hover:text-fg hover:underline"
              >
                Clear all
              </button>
            ) : null}
          </div>

          <div className="h-[calc(17rem+2.5rem)] overflow-auto overscroll-contain rounded-md border border-line bg-s2 p-2 md:h-[calc(17rem+2.25rem)]">
            {chosen.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
                <Building2 aria-hidden="true" className="size-5 text-faint" />
                <p className="text-[12.5px] text-faint">
                  Pick a city and its warehouses appear here.
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {chosen.map((city) => (
                  <WarehouseCard
                    key={city.key}
                    city={city}
                    picked={p.warehouses[city.key] ?? []}
                    dispatch={p.dispatch}
                  />
                ))}
              </div>
            )}
          </div>

          {chosen.length > 0 ? (
            <p className="text-[11.5px] text-faint tabular">
              {chosen.length} {chosen.length === 1 ? 'city' : 'cities'} · {totalSites}{' '}
              {totalSites === 1 ? 'warehouse' : 'warehouses'}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  )
})

const CityRow = React.memo(
  function CityRow(p: { city: CityOption; checked: boolean; dispatch: React.Dispatch<Action> }) {
    return (
      <label
        className={cn(
          'flex cursor-pointer items-center gap-2.5 rounded-sm px-2 py-1',
          p.checked ? 'bg-accent-soft' : 'hover:bg-s3'
        )}
      >
        <Checkbox
          checked={p.checked}
          onCheckedChange={() =>
            p.dispatch({ type: 'toggleCity', key: p.city.key, sites: p.city.sites })
          }
          aria-label={p.city.label}
          className="min-h-0"
        />
        <span className={cn('min-w-0 flex-1 truncate text-[13px]', p.checked ? 'text-fg' : 'text-muted')}>
          {p.city.label}
        </span>
        <span className="flex-none text-[11px] text-faint tabular">{p.city.sites.length}</span>
      </label>
    )
  },
  (a, b) => a.checked === b.checked && a.city === b.city && a.dispatch === b.dispatch
)

/**
 * One city's sites, all of them selected the moment the city is.
 *
 * Clicking one site removes exactly that site. It used to mean "only this one",
 * which read as a sensible narrowing gesture and was wrong for the actual job:
 * the operator has a list of warehouses to exclude, not one to keep, so nine
 * clicks were needed to drop one.
 *
 * There is no "empty means all" state. Deselecting the last warehouse leaves a
 * city with none, which is an error the form reports — rather than silently
 * flipping back to every site, which is the opposite of what the click asked
 * for and would be stored as such.
 */
const WarehouseCard = React.memo(
  function WarehouseCard({
    city,
    picked,
    dispatch,
  }: {
    city: CityOption
    picked: string[]
    dispatch: React.Dispatch<Action>
  }) {
    const all = picked.length === city.sites.length
    const none = picked.length === 0
    const pickedSet = React.useMemo(() => new Set(picked), [picked])

    // Destructured rather than read off `p`: listing the props object as a
    // dependency rebuilds this on every render, which is the same as not
    // memoising it at all.
    const toggle = React.useCallback(
      (site: string) => {
        dispatch({ type: 'toggleSite', key: city.key, site, all: city.sites })
      },
      [dispatch, city.key, city.sites]
    )

    return (
      <div className={cn('rounded-md border bg-s1 p-2', none ? 'border-danger/50' : 'border-line')}>
        <div className="flex items-center gap-2 pb-1.5">
          <MapPin aria-hidden="true" className="size-3.5 flex-none text-faint" />
          <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium text-fg">
            {city.label}
          </span>
          <span className="flex-none text-[10.5px] text-faint tabular">
            {picked.length}/{city.sites.length}
          </span>
          {/* Re-select everything. Only offered when something is missing, so
              it is an action rather than a state indicator. */}
          {all ? null : (
            <button
              type="button"
              onClick={() => dispatch({ type: 'setSites', key: city.key, sites: [...city.sites] })}
              className="flex-none rounded-full border border-line px-2 py-0.5 text-[10.5px] font-medium text-muted transition-colors hover:border-line-strong hover:text-fg"
            >
              All {city.sites.length}
            </button>
          )}
          <button
            type="button"
            onClick={() => dispatch({ type: 'toggleCity', key: city.key, sites: city.sites })}
            aria-label={`Remove ${city.label}`}
            className="flex-none rounded-sm p-0.5 text-faint transition-colors hover:text-danger"
          >
            <X aria-hidden="true" className="size-3.5" />
          </button>
        </div>

        {none ? (
          <p className="pb-1 text-[11px] text-danger">
            Pick at least one warehouse, or remove the city.
          </p>
        ) : null}

        <div className="flex flex-wrap gap-1">
          {city.sites.map((site) => {
            // The selection is always explicit now — no `all ||` shortcut, which
            // is what made every chip look selected while the stored list was
            // empty.
            const on = pickedSet.has(site)
            return (
              <button
                key={site}
                type="button"
                onClick={() => toggle(site)}
                aria-pressed={on}
                className={cn(
                  'rounded border px-1.5 py-0.5 font-mono text-[10.5px] tabular transition-colors',
                  on
                    ? 'border-accent/40 bg-accent-soft text-accent'
                    : 'border-line text-faint hover:border-line-strong hover:text-muted'
                )}
              >
                {/* The "SITE-" prefix is on every code and carries nothing. */}
                {site.startsWith('SITE-') ? site.slice(5) : site}
              </button>
            )
          })}
        </div>
      </div>
    )
  },
  (a, b) => a.city === b.city && a.dispatch === b.dispatch && sameStrings(a.picked, b.picked)
)

function sameStrings(a: readonly string[], b: readonly string[]): boolean {
  if (a === b) return true
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false
  return true
}

/* ================================================================== *
 * The page
 * ================================================================== */

/**
 * Turn a loaded customer into a draft.
 *
 * The three write-only fields come back as presence flags and nothing else, so
 * they start blank and are only sent when the operator types a replacement —
 * which is exactly what the legacy screen's "leave blank to keep the saved
 * value" meant.
 */
function draftFromDetail(d: CustomerDetail): Draft {
  return {
    name: d.name,
    email: d.email,
    phone: d.phone ?? '',
    pin: '',
    imapAppPassword: '',
    cities: [...d.cities],
    warehouses: Object.fromEntries(Object.entries(d.cityWarehouses).map(([k, v]) => [k, [...v]])),
    // Filtered against the canonical spellings: a stored row could hold a
    // fourth value this build does not know, and a chip that cannot be turned
    // off is worse than one that is missing.
    workHours: WORK_HOURS.filter((h) => (d.workHours as string[]).includes(h)),
    // Rows older than the column arrive null: the control starts unpicked and
    // Save refuses until one is chosen — which is how such a row gets one.
    shiftPriority: d.shiftPriority ?? '',
    agentUsername: d.agentUsername ?? '',
    notes: d.notes ?? '',
    sinNumber: '',
    /*
     * Only a real `YYYY-MM-DD` seeds the field.
     *
     * The three boxes are driven by `splitIso`, which matches that shape and
     * nothing else, so anything else used to leave them BLANK while the draft
     * still carried the unusable value — and the form then refused to submit
     * with "Not a real date" printed under an empty-looking field, which no
     * operator can act on, and which blocked every unrelated edit to the whole
     * customer. Seeding `''` makes the state honest: the field is empty, the
     * message is "Required", and the hint below says why.
     */
    dateOfBirth: parseDob(d.dateOfBirth) === null ? '' : (d.dateOfBirth as string),
    gender: d.gender ?? '',
    addressLine1: d.addressLine1 ?? '',
    addressLine2: d.addressLine2 ?? '',
    addressCity: d.addressCity ?? '',
    addressProvince: d.addressState ?? '',
    addressPostalCode: d.addressZipcode ?? '',
    mode: d.mode,
  }
}

/** Create and edit are the same form; this is the difference between them. */
export function CreateCustomerPage(props: { customerId?: string } = {}) {
  const editingId = props.customerId ?? null
  const isEdit = editingId !== null

  const existing = useCustomerDetail(editingId)
  const update = useUpdateCustomer(editingId ?? '')

  const [draft, dispatch] = React.useReducer(reducer, EMPTY)
  const [touched, setTouched] = React.useState<Partial<Record<keyof Draft, true>>>({})
  const [submitted, setSubmitted] = React.useState(false)
  /**
   * What the SERVER said was wrong, per field.
   *
   * Kept apart from the local rules because it has a different lifetime: a
   * local error disappears the moment the value becomes valid, whereas "this
   * email is taken" stays true until the email is *changed*. Cleared per field
   * on edit, below.
   */
  const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({})

  /*
   * Seed the form once the customer arrives — and only once.
   *
   * Keyed on the customer's own id rather than on the query object: the detail
   * query refetches whenever the lists are invalidated, and re-seeding on every
   * response would throw away whatever the operator had typed since.
   */
  const seededFor = React.useRef<string | null>(null)
  React.useEffect(() => {
    const detail = existing.data
    if (!detail || seededFor.current === detail.id) return
    seededFor.current = detail.id
    dispatch({ type: 'load', draft: draftFromDetail(detail) })
  }, [existing.data])

  const cities = useCities()
  const agents = useAgentRoster()
  const create = useCreateCustomer()
  const { toast } = useToast()
  const navigate = useNavigate()

  /*
   * Only asked once the field has been left, so it is one request per completed
   * field rather than one per keystroke. The submit path checks again server-
   * side regardless; this exists to say "taken" while the cursor is still there,
   * instead of after eighteen more fields have been filled in.
   */
  const checkedEmail = touched.email || submitted ? draft.email : ''
  const checkedPhone = touched.phone || submitted ? draft.phone : ''
  const dupes = useDuplicateCheck(checkedEmail, checkedPhone)

  /*
   * A customer is not their own duplicate.
   *
   * On an edit the lookup finds the row being edited, so without this the form
   * would report "already registered to <themselves>" and refuse to save any
   * change at all — the server excludes them by `customer_id` for the same
   * reason.
   */
  /*
   * The record holds something in `date_of_birth` that is not a date.
   *
   * The field starts empty because of it (see `draftFromDetail`), and an
   * operator who is not told that will read the blank as "this customer never
   * had one" and will not know they are about to overwrite something.
   */
  const storedDob = existing.data?.dateOfBirth ?? null
  const dobUnreadable = storedDob !== null && parseDob(storedDob) === null

  const ownId = existing.data?.customerId ?? null
  const emailMatch = dupes.data?.email ?? null
  const phoneMatch = dupes.data?.phone ?? null
  const emailTaken = emailMatch && emailMatch.customerId !== ownId ? emailMatch : null
  const phoneSeen = phoneMatch && phoneMatch.customerId !== ownId ? phoneMatch : null

  const localErrors = React.useMemo(() => validate(draft, isEdit), [draft, isEdit])

  /** Local rules first, then anything the server has told us about a field. */
  const errors = React.useMemo((): Errors => {
    const merged: Errors = { ...localErrors }
    for (const [field, message] of Object.entries(serverErrors)) {
      if (field in EMPTY) merged[field as keyof Draft] ??= message
    }
    /*
     * The duplicate lookup is the same answer the submit would give, arriving
     * earlier — so it blocks here rather than letting the operator find out
     * after filling in the rest of the form.
     *
     * The message names WHO, because that is the difference between "start
     * over" and "you already did this one": the row is usually the same person
     * entered twice, and the id is what the operator needs to go and look.
     */
    if (emailTaken !== null && merged.email === undefined) {
      merged.email = `Already registered to ${emailTaken.name} — ${emailTaken.customerId}, ${emailTaken.status}`
    }
    return merged
  }, [localErrors, serverErrors, emailTaken])

  const errorCount = Object.keys(errors).length

  /** Show an error once the field has been left, or once Save was pressed. */
  const errorFor = React.useCallback(
    (field: keyof Draft): string | undefined =>
      submitted || touched[field] ? errors[field] : undefined,
    [errors, touched, submitted]
  )

  const set = React.useCallback((field: TextField, value: string) => {
    dispatch({ type: 'set', field, value })
    // Editing a field retracts the server's complaint about it. Leaving it up
    // would keep "Already on file" under an email the operator has just fixed.
    setServerErrors((prev) => {
      if (prev[field] === undefined) return prev
      const next = { ...prev }
      delete next[field]
      return next
    })
  }, [])

  const blur = React.useCallback((field: keyof Draft) => {
    setTouched((t) => (t[field] ? t : { ...t, [field]: true }))
  }, [])

  /** The repetitive prop set, in one place, so a field cannot be wired to the
   *  wrong state key by a copy-paste. */
  const text = React.useCallback(
    (field: TextField) => ({
      id: field,
      value: draft[field] as string,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => set(field, e.target.value),
      onBlur: () => blur(field),
      'aria-describedby': describedBy(field),
      'aria-invalid': errorFor(field) ? (true as const) : undefined,
      className: errorFor(field) ? 'border-danger focus-within:border-danger' : undefined,
    }),
    [draft, set, blur, errorFor]
  )

  /** Stable for the life of the form — see the note on `SecretField`. */
  const setSecret = React.useCallback(
    (field: TextField, value: string) => set(field, value),
    [set]
  )
  const setDateOfBirth = React.useCallback((iso: string) => set('dateOfBirth', iso), [set])
  const blurDateOfBirth = React.useCallback(() => blur('dateOfBirth'), [blur])

  /* ---------------- fill from message ----------------------------- */

  /*
   * The latest draft and touched map, readable from callbacks that must not
   * re-bind on every keystroke. Synced after commit, which is early enough:
   * every reader below runs from an event or a resolved request, never
   * mid-render.
   */
  const latest = React.useRef({ draft, touched })
  React.useEffect(() => {
    latest.current = { draft, touched }
  }, [draft, touched])

  /**
   * One fingerprint per render, shared by the dirty check and by Undo. The
   * draft is eighteen scalars and two small arrays, so this is cheap — and
   * both comparisons must read the same bytes, or one could disagree.
   */
  const draftJson = React.useMemo(() => JSON.stringify(draft), [draft])

  const features = useFeatures()
  const [autofillOpen, setAutofillOpen] = React.useState(false)
  /** Text a page-level paste brought along; the dialog reads it on open. */
  const [seedText, setSeedText] = React.useState<string | null>(null)
  const [summary, setSummary] = React.useState<AutofillSummary | null>(null)
  const formRef = React.useRef<HTMLFormElement>(null)
  const scrollerRef = React.useRef<HTMLDivElement>(null)

  const currentValues = React.useCallback((): AutofillValues => latest.current.draft, [])

  const openAutofill = React.useCallback((seed: string | null) => {
    setSeedText(seed)
    setAutofillOpen(true)
  }, [])

  const onAutofillOpenChange = React.useCallback((open: boolean) => {
    setAutofillOpen(open)
    if (!open) setSeedText(null)
  }, [])

  const applyAutofill = React.useCallback((result: AutofillResult, mode: ApplyMode) => {
    const { draft: before, touched: touchedBefore } = latest.current
    const plan = planAutofill(before, result.fields, mode)

    if (plan.fields.length > 0) {
      dispatch({ type: 'autofill', patch: plan.patch })
      /*
       * Marked touched, so the rules' verdict on what landed shows at once —
       * a five-digit PIN must not wait for a blur the operator has no reason
       * to perform. A server complaint about a field the message has just
       * rewritten is retracted, exactly as typing into it would.
       */
      setTouched((t) => {
        const next = { ...t }
        for (const field of plan.fields) next[field] = true
        return next
      })
      setServerErrors((prev) => {
        if (!plan.fields.some((field) => prev[field] !== undefined)) return prev
        const next = { ...prev }
        for (const field of plan.fields) delete next[field]
        return next
      })
    }

    setSummary({
      filled: plan.fields,
      skipped: result.skipped,
      before,
      touchedBefore,
      // The same spread the reducer performs, so the two fingerprints agree.
      after: JSON.stringify({ ...before, ...plan.patch }),
    })
    setAutofillOpen(false)
    setSeedText(null)
    flashFields(formRef.current, plan.fields)
    // The banner and the first fields are at the top; a paste made while
    // scrolled down would otherwise change the form out of sight.
    scrollerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const undoAutofill = React.useCallback(() => {
    if (summary === null) return
    dispatch({ type: 'load', draft: summary.before })
    setTouched(summary.touchedBefore)
    setSummary(null)
  }, [summary])

  const dismissAutofill = React.useCallback(() => setSummary(null), [])

  /*
   * Undo is offered only while the form still matches what the fill left it:
   * once the operator has typed anything since, a reset to `before` would
   * take their edits with it.
   */
  const canUndo = summary !== null && summary.filled.length > 0 && draftJson === summary.after

  /*
   * The zero-click path: paste anywhere on the page that is not a control and
   * the dialog opens already reading. Also the shortcut, for operators who
   * keep their hands on the keyboard. Armed on create only — an edit form is
   * already full, so nearly every value would be a conflict — and never while
   * the dialog is up, where the box has its own paste handling.
   */
  const autofillArmed = features.autofill && !isEdit && !autofillOpen
  React.useEffect(() => {
    if (!autofillArmed) return

    const onPaste = (event: ClipboardEvent): void => {
      // A control that has already taken the paste — the date boxes accept
      // a pasted date this way — keeps it.
      if (event.defaultPrevented) return
      const pasted = event.clipboardData?.getData('text') ?? ''
      // A few characters is a stray paste, not a message.
      if (pasted.trim().length < 8) return
      /*
       * A paste into a control belongs to that control — with one exception.
       * A single-line box cannot hold a multi-line message: the browser folds
       * the line breaks and the operator ends up with a Name field holding
       * the whole chat. That paste was meant for the form, so it goes to the
       * dialog instead; a textarea, a select and anything outside this form
       * are left alone.
       */
      if (isEditable(event.target)) {
        const box = event.target
        const singleLineHere =
          box instanceof HTMLInputElement && formRef.current?.contains(box) === true
        if (!singleLineHere || !looksLikeMessage(pasted)) return
      }
      event.preventDefault()
      openAutofill(pasted)
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || event.altKey) return
      if (!(event.metaKey || event.ctrlKey) || !event.shiftKey) return
      if (event.key.toLowerCase() !== 'v') return
      event.preventDefault()
      openAutofill(null)
    }

    document.addEventListener('paste', onPaste)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('paste', onPaste)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [autofillArmed, openAutofill])

  const busy = isEdit ? update.isPending : create.isPending

  /**
   * Has anything actually been changed?
   *
   * Compared against the loaded customer on an edit and against a blank form on
   * a create, so "Discard changes" is disabled when there is nothing to discard
   * and Cancel can warn only when leaving would actually lose something.
   * `JSON.stringify` is fine here: the draft is eighteen scalars and two small
   * arrays, and this runs once per render of a form, not per row of a table.
   */
  const baseline = React.useMemo(
    () => (existing.data ? draftFromDetail(existing.data) : EMPTY),
    [existing.data]
  )
  const dirty = React.useMemo(
    () => draftJson !== JSON.stringify(baseline),
    [draftJson, baseline]
  )

  /**
   * Back to the pending list. No arguments, so it can be a `onClick` handler
   * directly — Cancel passes a MouseEvent to whatever it is given.
   */
  const leave = React.useCallback(() => {
    void navigate({ to: '/customers/$status', params: { status: 'pending' }, search: {} })
  }, [navigate])

  /**
   * Back to the list the customer just saved is actually ON.
   *
   * The pending queue is split: a customer under a segmented agent is NOT in
   * `/customers/pending`, which excludes them. Landing there after a save would
   * show a list that visibly does not contain the person just created, under a
   * toast saying it does — the one dead end this split introduces.
   *
   * The agent arrives as an argument rather than off `draft`, so this callback
   * stays stable across every keystroke in the form.
   */
  const leaveToList = React.useCallback(
    (agentUsername: string | null) => {
      const segment =
        agentUsername === null ? undefined : SEGMENT_BY_AGENT_USERNAME[agentUsername.toLowerCase()]
      if (segment === undefined) {
        leave()
        return
      }
      void navigate({ to: '/customers/telegram', search: {} })
    },
    [navigate, leave]
  )

  const catalogue = cities.data ?? EMPTY_CITIES

  /* Wrapped so the picker's prop is stable — `cities.refetch` is a new function
     identity on some query-state transitions, which would re-render the list. */
  const { refetch: refetchCitiesQuery } = cities
  const refetchCities = React.useCallback(() => {
    void refetchCitiesQuery()
  }, [refetchCitiesQuery])

  const onSubmit = React.useCallback(
    (event: React.FormEvent) => {
      event.preventDefault()

      /*
       * A submit already in flight wins.
       *
       * The button shows a spinner and is disabled, but Enter in any text field
       * submits the form regardless — and on create this POST allocates two
       * sequence numbers from `meta` before it inserts. A double submit
       * therefore burns a pair of ids and races two inserts at the unique email
       * index; one wins and the operator sees an "already exists" error for a
       * customer they had just successfully created.
       */
      if (busy) return

      setSubmitted(true)

      // The memoised `errors` — recomputing here would run the same validation
      // a third time on every press for a result that cannot have changed.
      const failing = Object.keys(errors) as (keyof Draft)[]
      if (failing.length > 0) {
        // Move focus to the first thing that is wrong. Scrolling a long form to
        // an error the operator then has to find is half a fix.
        const first = failing[0]
        if (first) document.getElementById(first)?.focus()
        return
      }

      /*
       * Trimmed here, normalised on the server.
       *
       * The spaces in a pasted app password and the punctuation in a phone
       * number or a SIN are stripped once, in `customers.write`, so that a
       * value arriving from any client is stored the single way the collection
       * holds it. Doing it in both places would be two rules to keep in step.
       */
      const payload: CustomerDraft = {
        name: draft.name.trim(),
        email: draft.email.trim(),
        phone: draft.phone.trim(),
        pin: draft.pin.trim(),
        imapAppPassword: draft.imapAppPassword,
        cities: draft.cities,
        warehouses: draft.warehouses,
        workHours: draft.workHours,
        // `validate` has refused '' by here, so what is left is a member.
        shiftPriority: draft.shiftPriority as ShiftPriority,
        agentUsername: nullable(draft.agentUsername),
        notes: nullable(draft.notes),
        sinNumber: draft.sinNumber.trim(),
        dateOfBirth: draft.dateOfBirth,
        // '' means "not set", which the wire spells as null.
        gender: draft.gender === '' ? null : (draft.gender as Gender),
        addressLine1: draft.addressLine1.trim(),
        addressLine2: nullable(draft.addressLine2),
        addressCity: draft.addressCity.trim(),
        addressProvince: draft.addressProvince as Province,
        addressPostalCode: draft.addressPostalCode.trim().toUpperCase(),
        mode: draft.mode,
      }

      /*
       * Put the reason ON the field wherever the server named one.
       *
       * A toast is a notification, not a correction: it disappears, it is
       * nowhere near the input, and on the next attempt the operator hits the
       * same wall. `details[].field` lets "Already on file" sit under the email
       * box until the email changes.
       */
      const handleError = (err: unknown, title: string) => {
        const byField = fieldErrorsOf(err)
        if (Object.keys(byField).length > 0) {
          setServerErrors(byField)
          const first = Object.keys(byField)[0]
          if (first) document.getElementById(first)?.focus()
        }
        toast({
          title: err instanceof ApiError && err.status === 409 ? 'That email is taken' : title,
          description: friendlyError(err),
          tone: 'danger',
        })
      }

      if (isEdit) {
        /*
         * The three write-only fields are sent only when retyped.
         *
         * They come back from the API as presence flags, so the form shows them
         * blank — and sending that blank would either fail the shape rule or,
         * if the server were lenient about it, wipe a credential the operator
         * never touched.
         */
        const patch: CustomerEdit = { ...payload }
        if (draft.pin.trim() === '') delete patch.pin
        if (draft.imapAppPassword.trim() === '') delete patch.imapAppPassword
        if (draft.sinNumber.trim() === '') delete patch.sinNumber

        update.mutate(patch, {
          onSuccess: () => {
            toast({
              title: `${payload.name} saved`,
              description: 'The changes have been written.',
              tone: 'ok',
            })
            leaveToList(payload.agentUsername)
          },
          onError: (err) => handleError(err, 'Could not save the changes'),
        })
        return
      }

      create.mutate(payload, {
        onSuccess: (created) => {
          /*
           * From the PAYLOAD, not the response: `CustomerCreated` carries no
           * agent, and the id prefix that encodes it is not worth parsing back
           * out when the value we sent is right here.
           */
          const segment = payload.agentUsername
            ? SEGMENT_BY_AGENT_USERNAME[payload.agentUsername.toLowerCase()]
            : undefined
          toast({
            title: `${created.name} created`,
            description: `${created.customerId} — now in ${segment ? SEGMENT_LABEL[segment] : 'Pending'}.`,
            tone: 'ok',
          })
          leaveToList(payload.agentUsername)
        },
        onError: (err) => handleError(err, 'Could not create the customer'),
      })
    },
    [draft, errors, create, update, isEdit, busy, toast, leaveToList]
  )

  const blocked = submitted && errorCount > 0

  /*
   * An edit whose customer has not arrived yet must not be submittable.
   *
   * The form would otherwise render empty, fail every required rule, and blame
   * the operator for fields they have not been shown.
   */
  if (isEdit && existing.isLoading) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <p className="flex items-center gap-2 text-[13px] text-faint">
          <Loader2 aria-hidden="true" className="size-4 animate-spin" />
          Loading the customer…
        </p>
      </div>
    )
  }

  if (isEdit && existing.isError) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 text-center">
          <AlertTriangle aria-hidden="true" className="size-5 text-warn" />
          <p className="text-[13.5px] text-fg">This customer could not be loaded.</p>
          <p className="text-[12.5px] text-muted">{friendlyError(existing.error)}</p>
          <Button type="button" variant="secondary" size="sm" onClick={() => void existing.refetch()}>
            Try again
          </Button>
        </div>
      </div>
    )
  }

  return (
    <form ref={formRef} onSubmit={onSubmit} noValidate className="flex min-h-0 flex-1 flex-col">
      <div ref={scrollerRef} className="min-h-0 flex-1 overflow-auto overscroll-contain">
        <div className="mx-auto flex max-w-5xl flex-col gap-4 p-4 md:p-5">
          <header className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h1 className="font-display text-[19px] font-semibold tracking-[-0.014em] text-fg">
              {isEdit ? 'Edit customer' : 'Create customer'}
            </h1>
            {isEdit ? (
              // The id, because it is the thing every other screen and every
              // Mongo query refers to them by — and it is not editable.
              <p className="flex items-center gap-2 text-[12px] text-faint">
                <span className="font-mono text-[11.5px] text-muted">
                  {existing.data?.customerId ?? ''}
                </span>
                <span>the customer id never changes</span>
              </p>
            ) : (
              /* The same line the legacy screen carries, because it answers the
                 question the Save button raises: where does this go next? */
              <p className="flex items-center gap-1.5 text-[12px] text-faint">
                Saved as
                <span className="rounded-full border border-line px-1.5 py-0.5 text-[11px] font-medium text-muted">
                  Pending
                </span>
                <ArrowRight aria-hidden="true" className="size-3" />
                approve
                <ArrowRight aria-hidden="true" className="size-3" />
                start
                <ArrowRight aria-hidden="true" className="size-3" />
                confirmed
              </p>
            )}

            {/* Create only. An edit form is already full, so a message would
                conflict with nearly every field — see `autofillArmed`. */}
            {isEdit ? null : (
              <div className="ml-auto">
                <AutofillButton available={features.autofill} onClick={() => openAutofill(null)} />
              </div>
            )}
          </header>

          {isEdit ? null : (
            <AutofillDialog
              open={autofillOpen}
              onOpenChange={onAutofillOpenChange}
              seedText={seedText}
              currentValues={currentValues}
              onApply={applyAutofill}
            />
          )}

          {summary === null ? null : (
            <AutofillBanner
              summary={summary}
              canUndo={canUndo}
              onUndo={undoAutofill}
              onDismiss={dismissAutofill}
            />
          )}

          <Section title="Identity" hint="Who the application is for" icon={<UserPlus />}>
            <Field id="name" label="Name" required error={errorFor('name')}>
              <Input {...text('name')} placeholder="Full name, as on their ID" autoComplete="off" />
            </Field>

            {/* A shared number is legitimate — one pair in the whole collection
                shares one, and it is the same person recorded twice — so this
                is a caution beside the field and never a blocked submit. */}
            <Field id="phone" label="Phone number" error={errorFor('phone')}>
              <Input {...text('phone')} type="tel" placeholder="+1 234 567 8900" autoComplete="off" />
              {phoneSeen === null ? null : (
                <p className="mt-1.5 flex items-start gap-1 text-[11.5px] leading-snug text-warn">
                  <AlertTriangle aria-hidden="true" className="mt-[3px] size-3 flex-none" />
                  <span>
                    Also on file for <strong className="font-medium">{phoneSeen.name}</strong> (
                    {phoneSeen.customerId}, {phoneSeen.status}). You can still continue.
                  </span>
                </p>
              )}
            </Field>

            <Field id="email" label="Email" required error={errorFor('email')}>
              <Input
                {...text('email')}
                type="email"
                inputMode="email"
                spellCheck={false}
                placeholder="name@gmail.com"
                autoComplete="off"
              />
            </Field>

            {/* Write-only, and in this card rather than a section of their own:
                two fields did not justify a second heading, and they are part
                of identifying the customer, not a separate activity. */}
            <Field
              id="pin"
              label="PIN"
              required={!isEdit}
              error={errorFor('pin')}
              hint={
                isEdit
                  ? 'Leave blank to keep the stored PIN.'
                  : 'Six digits — the Amazon hiring account PIN.'
              }
            >
              <SecretField
                id="pin"
                value={draft.pin}
                invalid={errorFor('pin') !== undefined}
                placeholder="••••••"
                onChange={setSecret}
                onBlur={blur}
              />
            </Field>

            <Field
              id="imapAppPassword"
              label="Gmail app password"
              required={!isEdit}
              error={errorFor('imapAppPassword')}
              hint={
                isEdit
                  ? 'Leave blank to keep the stored password.'
                  : 'From Google App Passwords — spaces are removed for you. The one-time code is read out of this inbox, so a run cannot start without it.'
              }
            >
              <SecretField
                id="imapAppPassword"
                value={draft.imapAppPassword}
                invalid={errorFor('imapAppPassword') !== undefined}
                placeholder="abcd efgh ijkl mnop"
                onChange={setSecret}
                onBlur={blur}
              />
            </Field>

            {/* Last in the card: it is the one field here that is about our
                bookkeeping rather than about the person, and it is the one
                that cannot be changed afterwards. */}
            <Field
              id="agentUsername"
              label="Agent"
              required
              error={errorFor('agentUsername')}
              hint="Sets the customer id prefix and cannot be changed afterwards."
              wide
            >
              <select
                id="agentUsername"
                value={draft.agentUsername}
                onChange={(e) => set('agentUsername', e.target.value)}
                onBlur={() => blur('agentUsername')}
                aria-describedby={describedBy('agentUsername')}
                aria-invalid={errorFor('agentUsername') ? true : undefined}
                className={cn(CONTROL, errorFor('agentUsername') && 'border-danger')}
              >
                <option value="">Select…</option>
                {(agents.data ?? []).map((a) => (
                  <option key={a.username} value={a.username}>
                    {a.name ? `${a.name} (${a.username})` : a.username}
                  </option>
                ))}
              </select>
            </Field>
          </Section>

          <Section
            title="Where they will work"
            hint="Cities and the warehouses to watch"
            icon={<Building2 />}
          >
            <CityPicker
              catalogue={catalogue}
              loading={cities.isLoading}
              failed={cities.isError}
              onRetry={refetchCities}
              selected={draft.cities}
              warehouses={draft.warehouses}
              dispatch={dispatch}
              error={errorFor('cities')}
            />

            <Field id="workHours" label="Work hours" required error={errorFor('workHours')} wide>
              {/* `id` on the group so it can be found like any other field —
                  by the error focus and by the fill's flash — and `tabIndex`
                  so that focus has somewhere to land. */}
              <div
                id="workHours"
                role="group"
                aria-labelledby="workHours-label"
                aria-describedby={describedBy('workHours')}
                tabIndex={-1}
                className="flex flex-wrap gap-1.5 rounded-md"
              >
                {WORK_HOURS.map((h) => {
                  const on = draft.workHours.includes(h)
                  return (
                    <button
                      key={h}
                      type="button"
                      onClick={() => dispatch({ type: 'toggleHours', value: h })}
                      aria-pressed={on}
                      className={cn(PILL, on ? PILL_ON : PILL_OFF)}
                    >
                      {on ? <Check aria-hidden="true" className="size-3" /> : null}
                      {h}
                    </button>
                  )
                })}
              </div>
            </Field>

            {/*
              * Single-select, and required beside any choice of hours.
              *
              * It only means something next to Full-time — a flex worker does
              * not pick a night shift — but the sibling panel that shares the
              * column asks for it regardless, and the two apps agreeing on what
              * a complete record looks like is worth one extra click on a
              * part-time customer. "Any" is that click.
              */}
            <Field
              id="shiftPriority"
              label="Shift priority (Works for Full-time Only)"
              required
              error={errorFor('shiftPriority')}
              hint={
                isEdit && existing.data?.shiftPriority === null
                  ? 'Not on file for this customer yet — pick one to save.'
                  : 'Which shifts to take when the hours are Full-time. Recorded either way.'
              }
              wide
            >
              <div
                id="shiftPriority"
                role="group"
                aria-labelledby="shiftPriority-label"
                aria-describedby={describedBy('shiftPriority')}
                tabIndex={-1}
                className="flex flex-wrap gap-1.5 rounded-md"
              >
                {SHIFT_PRIORITIES.map((option) => {
                  const on = draft.shiftPriority === option
                  return (
                    <button
                      key={option}
                      type="button"
                      // Radio behaviour: picking replaces, re-clicking keeps —
                      // the field is required, so there is no unpicked state
                      // to return to.
                      onClick={() => set('shiftPriority', option)}
                      aria-pressed={on}
                      className={cn(PILL, on ? PILL_ON : PILL_OFF)}
                    >
                      {on ? <Check aria-hidden="true" className="size-3" /> : null}
                      {option}
                    </button>
                  )
                })}
              </div>
            </Field>
          </Section>

          <Section title="Personal details" hint="Used to complete the application">
            <Field
              id="sinNumber"
              label="SIN number"
              required={!isEdit}
              error={errorFor('sinNumber')}
              hint={isEdit ? 'Leave blank to keep the stored number.' : undefined}
            >
              <Input
                {...text('sinNumber')}
                inputMode="numeric"
                placeholder={isEdit ? '•••••••••' : '123456789'}
                autoComplete="off"
              />
            </Field>

            <Field
              id="dateOfBirth"
              label="Date of birth"
              required
              hint={
                dobUnreadable
                  ? 'The stored value is not a YYYY-MM-DD date, so this starts empty. Re-enter it from the ID.'
                  : undefined
              }
              error={errorFor('dateOfBirth')}
            >
              <DateOfBirthField
                id="dateOfBirth"
                value={draft.dateOfBirth}
                invalid={errorFor('dateOfBirth') !== undefined}
                onChange={setDateOfBirth}
                onBlur={blurDateOfBirth}
              />
            </Field>

            {/*
              * Optional here, required before the audio assessment.
              *
              * Not marked `required`, deliberately: making it so would block
              * every create and edit on a field that has been blank on 747 of
              * 758 existing rows. The assessment refuses by name instead, which
              * is a better place to learn it — and this is where it gets fixed.
              *
              * It picks the voice a recorded answer is spoken in. There is no
              * default, because a recording that does not sound like the
              * applicant is worse for them than a run that stopped and said why.
              */}
            <Field
              id="gender"
              label="Gender"
              hint="Chooses the assessment voice"
            >
              <select
                id="gender"
                value={draft.gender}
                onChange={(e) => set('gender', e.target.value)}
                onBlur={() => blur('gender')}
                aria-describedby={describedBy('gender')}
                className={CONTROL}
              >
                <option value="">Not set</option>
                {GENDERS.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </Field>

            <Field id="addressLine1" label="Address line 1" required error={errorFor('addressLine1')} wide>
              <Input {...text('addressLine1')} placeholder="Street address" autoComplete="off" />
            </Field>

            {/* One of the two fields that may be left blank. */}
            <Field id="addressLine2" label="Address line 2" hint="Optional" wide>
              <Input {...text('addressLine2')} placeholder="Apt, suite, unit…" autoComplete="off" />
            </Field>

            <Field id="addressCity" label="City" required error={errorFor('addressCity')}>
              <Input {...text('addressCity')} placeholder="Scarborough" autoComplete="off" />
            </Field>

            <Field id="addressProvince" label="Province" required error={errorFor('addressProvince')}>
              <select
                id="addressProvince"
                value={draft.addressProvince}
                onChange={(e) => set('addressProvince', e.target.value)}
                onBlur={() => blur('addressProvince')}
                aria-describedby={describedBy('addressProvince')}
                aria-invalid={errorFor('addressProvince') ? true : undefined}
                className={cn(CONTROL, errorFor('addressProvince') && 'border-danger')}
              >
                <option value="">Select…</option>
                {PROVINCES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </Field>

            <Field id="addressPostalCode" label="Postal code" required error={errorFor('addressPostalCode')}>
              <Input {...text('addressPostalCode')} placeholder="M1M 2Z9" autoComplete="off" />
            </Field>

            <Field id="mode" label="Mode" hint="Leave at 3 unless you have been told otherwise.">
              <select
                id="mode"
                value={draft.mode}
                onChange={(e) => dispatch({ type: 'mode', value: e.target.value as Mode })}
                aria-describedby={describedBy('mode')}
                className={CONTROL}
              >
                {MODES.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </Field>

            <Field id="notes" label="Notes" hint="Optional" wide>
              <textarea
                id="notes"
                value={draft.notes}
                rows={2}
                maxLength={2000}
                onChange={(e) => set('notes', e.target.value)}
                aria-describedby={describedBy('notes')}
                placeholder="Anything the operator running this should know."
                className={cn(CONTROL, 'h-auto resize-y py-2 leading-snug')}
              />
            </Field>
          </Section>
        </div>
      </div>

      {/* ── action bar ───────────────────────────────────────────────
          A footer sibling of the scroller, not a sticky element inside it: a
          sticky bar with content after it covers that content for the whole
          scroll and then jumps above it at the end. Same shape as /settings. */}
      <div className="flex-none border-t border-line bg-s1">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 md:px-5">
          <Button type="submit" variant="primary" loading={busy}>
            {busy ? null : isEdit ? <Check aria-hidden="true" /> : <UserPlus aria-hidden="true" />}
            {isEdit ? 'Save changes' : 'Create customer'}
          </Button>

          {/* Leaves the page. Distinct from "Discard changes", which stays here
              and puts the stored values back — an operator who wants out of a
              form they opened by mistake should not have to find the browser's
              back button. */}
          {isEdit ? (
            <Button type="button" variant="secondary" disabled={busy} onClick={leave}>
              Cancel
            </Button>
          ) : null}

          <Button
            type="button"
            variant="ghost"
            disabled={busy || !dirty}
            title={dirty ? undefined : 'Nothing has been changed'}
            onClick={() => {
              if (isEdit) {
                // Discard means "back to what is stored", not "blank the form".
                if (existing.data) dispatch({ type: 'load', draft: draftFromDetail(existing.data) })
                setServerErrors({})
              } else {
                dispatch({ type: 'reset' })
              }
              setTouched({})
              setSubmitted(false)
              // A cleared form has nothing for the banner's Undo to restore.
              setSummary(null)
            }}
          >
            {isEdit ? 'Discard changes' : 'Clear'}
          </Button>

          {blocked ? (
            <p className="flex items-center gap-1.5 text-[12.5px] text-danger">
              <AlertTriangle aria-hidden="true" className="size-3.5" />
              {errorCount === 1 ? '1 field needs attention' : `${errorCount} fields need attention`}
              <span className="text-faint">
                ({(Object.keys(errors) as (keyof Draft)[]).map((k) => FIELD_LABEL[k] ?? k).join(', ')})
              </span>
            </p>
          ) : (
            <p className="text-[12.5px] text-faint">
              {isEdit
                ? 'Stays in Pending. Blank credential boxes keep their stored values.'
                : 'Lands in Pending. Nothing runs until it is approved and started.'}
            </p>
          )}
        </div>
      </div>
    </form>
  )
}

/**
 * Frozen so an absent catalogue is the same reference on every render.
 *
 * `cities.data ?? []` would allocate a new array each time, which defeats the
 * picker's memo comparator on every keystroke in every other field on the page.
 */
const EMPTY_CITIES: readonly CityOption[] = Object.freeze([])

/**
 * Today, read per render rather than at module load.
 *
 * A console is left open for a whole shift, and a constant captured at import
 * would let the date picker accept today once the clock passes midnight — the
 * one value `validate` then rejects, so the field would look accepted and the
 * form would refuse to submit with no visible reason.
 */
function today(): string {
  return new Date().toISOString().slice(0, 10)
}

/**
 * Is this a date that exists?
 *
 * `Date.parse('2026-02-31')` succeeds and yields 3 March, so parsing alone
 * cannot answer the question. Formatting the result back and comparing is what
 * rejects a day the month does not have.
 */
function isRealDate(iso: string): boolean {
  const ms = Date.parse(`${iso}T00:00:00Z`)
  if (Number.isNaN(ms)) return false
  return new Date(ms).toISOString().slice(0, 10) === iso
}

function nullable(v: string): string | null {
  const trimmed = v.trim()
  return trimmed === '' ? null : trimmed
}

/* ================================================================== *
 * Fill from message
 *
 * The dialog reads; the page writes. Everything that touches the draft lives
 * in the page component above, next to the reducer it drives, and these are
 * the pieces that have no reason to re-render with it.
 * ================================================================== */

/** What the last fill did, for the banner and its Undo. */
interface AutofillSummary {
  filled: AutofillKey[]
  skipped: AutofillSkipped[]
  /** The whole draft as it was the instant before — Undo is one `load`. */
  before: Draft
  /** And which fields had been visited, so Undo does not leave the blanks it restores flagged. */
  touchedBefore: Partial<Record<keyof Draft, true>>
  /** The draft as the fill left it. Undo is offered only while the form still matches. */
  after: string
}

/** A paste that lands here is meant for that control, not for the form. */
function isEditable(target: EventTarget | null): target is HTMLElement {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

/**
 * More than one line, once trailing whitespace is gone.
 *
 * Trimmed first because a line copied out of a chat usually ends in a newline,
 * and "John Smith⏎" pasted into Name is a name, not a message.
 */
function looksLikeMessage(text: string): boolean {
  return text.trim().includes('\n')
}

/**
 * Lights each filled field up in form order, a beat apart, so the eye is led
 * down the form to everything that changed. Plain DOM on purpose: the fields
 * are found by the same ids the error focus uses, and an attribute that
 * removes itself when its animation ends leaves no state to reconcile.
 */
function flashFields(form: HTMLFormElement | null, fields: readonly AutofillKey[]): void {
  if (form === null || fields.length === 0) return
  // After React has painted the new values, so the ring lands on them.
  requestAnimationFrame(() => {
    fields.forEach((field, i) => {
      const el = form.querySelector<HTMLElement>(`#${CSS.escape(field)}`)
      if (!el) return
      /*
       * The ring belongs on the box the operator sees. An `Input` draws its
       * border on the wrapper around the `<input>`, and the date's id sits on
       * its first segment inside the group — so for an input the parent is
       * the box; a select, a textarea and the pill groups are their own.
       */
      const target = el instanceof HTMLInputElement ? (el.parentElement ?? el) : el
      target.removeAttribute('data-autofill-flash')
      void target.offsetWidth // restart the animation if one is mid-flight
      target.style.setProperty('--flash-delay', `${i * 55}ms`)
      target.setAttribute('data-autofill-flash', '')
      const onEnd = (event: AnimationEvent): void => {
        // A child's animation bubbles through here too; only our own counts.
        if (event.target !== target) return
        target.removeEventListener('animationend', onEnd)
        target.removeAttribute('data-autofill-flash')
        target.style.removeProperty('--flash-delay')
      }
      target.addEventListener('animationend', onEnd)
    })
  })
}

/**
 * What the fill did, kept on screen rather than in a toast: it carries Undo,
 * and the things the message had that could not be used — a phone of the
 * wrong length, a date that is not a date — which the operator needs to act
 * on, not glimpse. Dismissed by hand, by Undo, by Clear, or by leaving.
 */
function AutofillBanner(p: {
  summary: AutofillSummary
  canUndo: boolean
  onUndo: () => void
  onDismiss: () => void
}) {
  const n = p.summary.filled.length
  const labels = p.summary.filled.map((field) => AUTOFILL_LABEL[field])
  const shown = labels.slice(0, 5).join(', ')
  const more = labels.length - 5
  const notes = p.summary.skipped.map((s) => s.reason)

  return (
    <div className="panel-in rounded-lg border border-accent/30 bg-accent-soft px-4 py-3">
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="grid size-8 flex-none place-items-center rounded-md bg-accent text-accent-fg"
        >
          <Sparkles className="size-4" />
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-semibold text-fg">
            {n === 0 ? 'Already filled in' : `Filled ${n} ${n === 1 ? 'field' : 'fields'} from the message`}
          </p>
          <p className="mt-0.5 text-[12.5px] leading-snug text-muted">
            {n === 0
              ? 'The form already had everything the message contained.'
              : `${shown}${more > 0 ? ` and ${more} more` : ''}. Give them a glance, then pick the agent, the cities and the warehouses.`}
          </p>
          {notes.length > 0 ? (
            <ul className="mt-2 space-y-1">
              {notes.map((note) => (
                <li key={note} className="flex items-start gap-1.5 text-[12px] font-medium text-warn">
                  <AlertTriangle aria-hidden="true" className="mt-px size-3.5 flex-none" />
                  {note}
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        {p.canUndo ? (
          <Button type="button" variant="ghost" size="sm" onClick={p.onUndo} className="flex-none">
            <Undo2 aria-hidden="true" />
            Undo
          </Button>
        ) : null}
        <button
          type="button"
          onClick={p.onDismiss}
          aria-label="Dismiss"
          className="-mr-1 flex size-8 flex-none items-center justify-center rounded-md text-faint transition-colors hover:bg-s3 hover:text-fg"
        >
          <X aria-hidden="true" className="size-4" />
        </button>
      </div>
    </div>
  )
}
